import { Close } from '@mui/icons-material';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';

interface StyledDrawerProps {
  open: boolean;
  onClose: () => void;
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  children: React.ReactNode;
  width?: number | string;
  hideBackdrop?: boolean;
  showHeader?: boolean;
  customHeader?: React.ReactNode;
  showFooter?: boolean;
  customFooter?: React.ReactNode;
  headerTitle?: string;
}

const StyledDrawer: React.FC<StyledDrawerProps> = ({
  open,
  onClose,
  anchor = 'right',
  children,
  width = 320,
  hideBackdrop = true,
  showHeader = false,
  customHeader,
  showFooter = false,
  customFooter,
  headerTitle,
}) => {
  const { translateHubUi } = useHubTranslation();
  const theme = useTheme();

  const headerSx = {
    px: 3,
    py: 2.5,
    borderBottom: `1px solid ${theme.palette.divider}`,
    mb: 2,
  };

  const footerSx = {
    py: 2,
    px: 3,
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
  };

  const defaultHeader = (
    <Box
      sx={{
        ...headerSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant='h6'>{headerTitle}</Typography>
      <IconButton
        aria-label={translateHubUi('common.drawer.closeAria', 'close drawer')}
        onClick={onClose}
        size='small'
      >
        <Close />
      </IconButton>
    </Box>
  );

  const defaultFooter = (
    <Box sx={footerSx}>
      <Button variant='outlined' color='primary' onClick={onClose}>
        Cancel
      </Button>
      <Button variant='contained' color='primary'>
        Apply
      </Button>
    </Box>
  );

  const renderHeader = () => {
    if (customHeader) return <Box sx={headerSx}>{customHeader}</Box>;
    if (showHeader) return defaultHeader;
    return null;
  };

  const renderFooter = () => {
    if (customFooter) return <Box sx={footerSx}>{customFooter}</Box>;
    if (showFooter) return defaultFooter;
    return null;
  };

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width,
          boxShadow: theme.shadows[6],
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      hideBackdrop={hideBackdrop}
    >
      {renderHeader()}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 0,
          px: 3,
        }}
      >
        {children}
      </Box>
      {renderFooter()}
    </Drawer>
  );
};

export default StyledDrawer;
